// ChatSourceChip — ONE cited source under an assistant answer (VaultOS v2
// design §4.2). Rendered by ChatMessage's `Sources` row via
// `childExtends: 'ChatSourceChip'`, one chip per entry of the assistant
// message's `sources` array.
//
// Row shape (the server's persisted `metadata.sources[]`):
//   { ref, type, id, title, use, verified }
// `ref` is the `ref:<type>:<id>` string form; `use` is the model's own
// one-line reason for citing it ("warranty cap, cl. 9"); `verified: false`
// marks a ref the turn's retrieval never actually opened. An unverified ref
// is KEPT and marked, never hidden: a claimed source the tools did not read
// is information, and dropping it would make an ungrounded answer look
// identical to a grounded one.
//
// It is a SEPARATE component, not an inline `childExtends: {}`, so a consumer
// can override the chip (FA808) and so the whole entity-ref grammar stays in
// one place. It deliberately does NOT extend AiContextChip: that component's
// click is bound to the alterable-reference mute toggle and its title factory
// renders the mute sentence, so a navigating chip with a `use` caption would
// be fighting it rather than reusing it.
//
// ── WHERE A CHIP GOES, AND WHAT IT DOES WHEN IT CANNOT GO (design §3.4/§3.5)
//
// A `record` opens on a MODULE page, resolved through the record-route
// registry the installed modules declare (shared/functions/entityRef.js
// `entityRefHref`). A module route must be opened with the shell's own opener,
// `openAppPage` — a raw `el.router('/vault-rooms?item=…')` is a HARD
// navigation to a blank page on the branded host (measured 2026-09-09). Every
// SHELL route (doc/mail/ticket/file/event/channel) keeps `el.router`, which is
// what it has always used and what the shell serves directly.
//
// And a chip whose ref this workspace cannot open is never dead-silent: it
// keeps its place, prints its KIND when the producer sent no title, says why
// in words, and hides the affordance by DISABLING the button rather than
// swallowing a click. A citation the reader cannot follow is still
// information; a control that does nothing is a lie.
//
// FA204 / frank trap — no module-scope const is read from any factory below;
// every entity-ref helper is reached with `el.call`, a runtime string lookup
// that survives frank.toJSON. The three derivations every factory needs live
// in `scope:` for the same reason (`el.scope.X` is the form frank rewrites to).
export const ChatSourceChip = {
  tag: 'button',
  type: 'button',
  scope: {
    // The chip may carry the string form, the flattened row, or both.
    raw(el, s) {
      return s.ref || { type: s.type, id: s.id, meta: s.meta }
    },
    refType(el, s) {
      try {
        const parsed = el.call('parseEntityRef', el.scope.raw(el, s))
        return (parsed && parsed.type) || ''
      } catch (_) {
        return ''
      }
    },
    // The destination, or null when nothing in this workspace opens it.
    href(el, s) {
      try {
        return el.call('entityRefHref', el.scope.raw(el, s), el.getRootState())
      } catch (_) {
        return null
      }
    },
    // A canvas node is NOT unreadable — it has no route by design and is
    // re-focused on the open board instead (spec §4.6).
    isUnreadable(el, s) {
      return el.scope.refType(el, s) !== 'canvas' && !el.scope.href(el, s)
    },
    // WHAT the chip prints: the producer's own title, else the KIND. Never an
    // id — the Longbow board's "Mail thread f82cc8" is what an id-as-title
    // looks like to a reader (proposal §0 fact 4). `s.title` is tenant content
    // and passes through `translate()` unchanged, so one call serves both.
    label(el, s) {
      return s.title
        ? String(s.title)
        : el.call('polyglot', el.call('entityRefKindKey', el.scope.raw(el, s)))
    },
    // The label wrapped in the sentence that says what clicking does — or why
    // nothing will. The key carries a {name} hole so a translation orders the
    // two parts its own way.
    sentence(el, s) {
      const key = el.scope.isUnreadable(el, s) ? 'ai.sources.unreadableTitle' : 'ai.sources.open'
      return el.call('polyglot', key).replace('{name}', el.scope.label(el, s))
    }
  },
  flow: 'x',
  align: 'center start',
  gap: 'Y',
  padding: 'Y Z',
  round: 'radiusPill',
  // Borderless, fill-contrast separation (design-direction §8): the
  // house `overlay` wash, the house `hover` step on approach. No ring,
  // no shadow — the shadow budget belongs to the drawer, not to a chip.
  background: 'overlay',
  borderWidth: '0',
  color: 'title',
  fontSize: 'Y2',
  // An unopenable chip is not a control: no pointer, no hover step, out of
  // the tab order, `aria-disabled` for the reader. The BOX is unchanged, so
  // nothing beside it moves (Rule 66) — only the affordance goes.
  cursor: (el, s) => (el.scope.isUnreadable(el, s) ? 'default' : 'pointer'),
  disabled: (el, s) => (el.scope.isUnreadable(el, s) ? true : null),
  maxWidth: '100%',
  minWidth: '0',
  textAlign: 'left',
  transition: 'B defaultBezier background',
  // Focus is NOT declared here: the global brand ring owns it
  // (DESIGN_LANGUAGE §8.2 — an `outline:` shorthand carrying a token letter
  // does not resolve, and that dead-ring class killed 20+ focus rings twice).
  ':hover': (el, s) => (el.scope.isUnreadable(el, s) ? {} : { background: 'hover' }),
  // An unverified ref steps back to muted ink — the label stays fully
  // legible, the status is unmistakable, no strike-through noise (the
  // same grammar AiContextChip uses for a dropped item).
  isUnverified: (el, s) => s.verified === false,
  // D-2 (2026-09-24): this text can sit on a washed dark row, where `inactive`
  // reads 4.29:1 on `hover` and 3.33:1 on `selected` — both under 4.5 — so the
  // quiet ink here is `caption`. (The chip washes `hover` on hover.)
  '.isUnverified': { color: 'caption' },
  // The name the reader hears, and the sentence around it. `{name}` is the
  // same label the chip prints — the ref's own title, else its KIND — so the
  // accessible name and the visible one can never disagree. An unopenable
  // chip must NOT be announced as "Open …".
  aria: {
    label: (el, s) => el.scope.sentence(el, s),
    disabled: (el, s) => (el.scope.isUnreadable(el, s) ? 'true' : null)
  },
  // A tooltip where it carries new information: on an unopenable chip the
  // sentence is the only place the reason appears for a pointer user; on an
  // openable one the chip's ONE line may be cut (a long document name, the
  // "why"), so the tooltip is the whole of it — the overflow law's condition
  // for an ellipsis (§3b rule 8).
  title: (el, s) => {
    if (el.scope.isUnreadable(el, s)) return el.scope.sentence(el, s)
    const use = s.use ? ' · ' + String(s.use) : ''
    return el.scope.label(el, s) + use
  },
  Icon: {
    name: (el, s) => el.call('entityRefIcon', s.ref || s),
    boxSize: 'iconSm',
    flexShrink: '0',
    color: 'currentColor'
  },
  Label: {
    tag: 'span',
    text: (el, s) => el.scope.label(el, s),
    fontWeight: '500',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: '0'
  },
  // WHY the source was cited. Quiet caption ink, same size as the label
  // so the row shares one baseline (Rule 34 — aligned siblings share
  // fontSize; both inherit the chip's own 'Y2').
  Use: {
    tag: 'span',
    if: (el, s) => !!s.use,
    text: (el, s) => '· ' + String(s.use),
    color: 'caption',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: '0'
  },
  Unverified: {
    tag: 'span',
    if: (el, s) => s.verified === false,
    text: '{{ ai.sources.unverified | polyglot }}',
    fontSize: 'X',
    color: 'caption',
    whiteSpace: 'nowrap',
    flexShrink: '0'
  },
  // §3.5 — one quiet line saying WHY nothing opens. The chip stays; only the
  // promise goes. The condition is fixed for the life of the chip (a ref does
  // not become readable mid-render), so this is not chrome that appears under
  // the pointer — no Rule 66 exposure.
  Unreadable: {
    tag: 'span',
    if: (el, s) => el.scope.isUnreadable(el, s),
    text: '{{ ai.sources.unreadable | polyglot }}',
    fontSize: 'X',
    color: 'caption',
    whiteSpace: 'nowrap',
    flexShrink: '0'
  },
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    const raw = el.scope.raw(el, s)
    const type = el.scope.refType(el, s)
    // A canvas node has no route of its own — it lives INSIDE an open
    // board, so the right act is to re-focus it there. `focusCanvasEntity`
    // is registered by the canvas surface only; everywhere else the try
    // fails and the chip is a no-op rather than a navigation to nowhere.
    if (type === 'canvas') {
      try {
        el.call('focusCanvasEntity', el.call('formatEntityRef', raw))
      } catch (_) {
        /* not on a canvas surface — no-op */
      }
      return
    }
    const href = el.scope.href(el, s)
    // No installed module claims this collection, and the producer supplied
    // no `meta.href`. The chip already SAYS so and its button is disabled —
    // this branch only catches a click that arrived some other way (keyboard
    // on an older bundle, a synthetic event).
    if (!href) return
    // A MODULE route must go through the shell's own opener: a plain
    // `el.router('/vault-rooms?item=…')` is a hard navigation to a blank page
    // on the branded host (measured 2026-09-09). `openAppPage` lands it in the
    // PageSlot exactly like a navbar tab, and `preferSlot` says so explicitly
    // rather than letting the opener re-derive it from boot-racy settings.
    // Every SHELL route keeps `el.router`, which is what serves them today.
    //
    // `record` is the one type whose home is a module page. If the opener is
    // not registered on this host (docs/preview), fall through to the router
    // rather than swallowing the click — degraded beats dead.
    if (type === 'record') {
      try {
        el.call('openAppPage', href, { preferSlot: true })
        return
      } catch (_) {
        /* no shell opener on this host — fall through to the router */
      }
    }
    try {
      el.router(href, el.getRoot())
    } catch (_) {
      /* no router on this host (docs/preview) — no-op */
    }
  }
}
